import { z } from 'zod';

import chainData from 'blockscope/static/chainData';

const {
  enrichedAddressSchema,
  enrichedBalanceSchema,
  evmTransactionHashSchema,
  evmAddressSchema,
  tokenDataSchema,
} = chainData;

const tracerListRow = z.object({
  _id: z.string(),
  alias: z.string(),
  createdBy: z.object({
    firstName: z.string(),
    lastName: z.string(),
  }),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  investigation: z
    .object({
      _id: z.string(),
      name: z.string(),
    })
    .optional(),
});
export const orgTracersSchema = z.array(tracerListRow);

const nodeTokenFlowSearchParams = z.object({
  chain: z.string(),
  filterAddress: evmAddressSchema,
  tokenAddress: tokenDataSchema,
  tokenType: z.enum(['native', 'erc20']),
  startTime: z.string().date(),
  endTime: z.string().date(),
});
/** @typedef {z.infer<typeof nodeTokenFlowSearchParams>} NodeTokenFlowSearchParams */

const nodeTransactionSearchParams = z.object({
  chain: z.string(),
  transactionHash: evmTransactionHashSchema,
});

const addressNodeData = z.object({
  address: enrichedAddressSchema,
  chain: z.string(),
  searchParams: z.array(
    z.union([nodeTokenFlowSearchParams, nodeTransactionSearchParams])
  ),
  interested: z.boolean().default(false),
});

const addressNode = z.object({
  nodeId: z.string(),
  type: z.literal('addressNode'),
  data: addressNodeData,
  savedPosition: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
});

const textNode = z.object({
  nodeId: z.string(),
  type: z.literal('textNode'),
  data: z.object({ text: z.string() }),
  savedPosition: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
  savedSize: z
    .object({
      width: z.number(),
      height: z.number(),
    })
    .optional(),
});

const groupNodeData = z.object({
  name: z.string(),
  members: z.array(addressNode),
});

const groupNode = z.object({
  nodeId: z.string(),
  type: z.literal('addressGroup'),
  data: groupNodeData,
  savedPosition: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
});

const edgeTokenFlowQuery = z.object({
  sourceAddress: enrichedAddressSchema,
  targetAddress: enrichedAddressSchema,
  amount: enrichedBalanceSchema,
  chain: z.string(),
  tokenAddress: tokenDataSchema,
  filterAddress: evmAddressSchema,
  startTime: z.string().date(),
  endTime: z.string().date(),
});
/** @typedef {z.infer<typeof edgeTokenFlowQuery>} EdgeTokenFlowQuery */

const edgeTransactionFlowQuery = z.object({
  sourceAddress: enrichedAddressSchema,
  targetAddress: enrichedAddressSchema,
  amount: enrichedBalanceSchema,
  chain: z.string(),
  transactionHash: evmTransactionHashSchema,
  tokenAddress: tokenDataSchema,
  txnTime: z.number(),
});
/** @typedef {z.infer<typeof edgeTransactionFlowQuery>} EdgeTransactionFlowQuery */

const genericEdge = z.object({
  edgeId: z.string(),
  type: z.literal('genericEdge'),
  source: z.string(),
  target: z.string(),
  data: z.object({
    queries: z.record(
      z.string(),
      z.union([edgeTokenFlowQuery, edgeTransactionFlowQuery])
    ),
  }),
});

const memberEdge = z.object({
  id: z.string(),
  type: z.literal('genericEdge'),
  source: z.string(),
  target: z.string(),
  data: z.object({
    queries: z.record(
      z.string(),
      z.union([edgeTokenFlowQuery, edgeTransactionFlowQuery])
    ),
  }),
});

const groupEdge = z.object({
  edgeId: z.string(),
  type: z.literal('groupEdge'),
  source: z.string(),
  target: z.string(),
  data: z.object({
    members: z.array(memberEdge),
  }),
});
const textEdge = z.object({
  edgeId: z.string(),
  source: z.string(),
  target: z.string(),
  type: z.literal('textEdge'),
});
export const tracerSettingsSchema = z.object({
  autoLayout: z.boolean().default(true),
  showUniqueTokens: z.boolean().default(true),
  showTimeRange: z.boolean().default(false),
  showEdgeValue: z.boolean().default(true),
  showChain: z.boolean().default(true),
  showHoldingValue: z.boolean().default(true),
  showAddressRisk: z.boolean().default(true),
  showMiniMap: z.boolean().default(false),
});
/** @typedef {z.infer<typeof tracerSettingsSchema>} TracerSettings */

const baseTracer = z.object({
  alias: z.string(),
  interestedAddresses: z.array(
    z.object({ address: enrichedAddressSchema, chain: z.string() })
  ),
  nodes: z.array(z.union([addressNode, groupNode, textNode])),
  edges: z.array(z.union([genericEdge, groupEdge, textEdge])),
  // settings
  settings: tracerSettingsSchema,
});

/** @typedef {z.infer<typeof baseTracer>} BaseTracer */

export const savedTracerSchema = baseTracer.extend({
  _id: z.string(),
  orgId: z.string(),
  createdBy: z.object({
    firstName: z.string(),
    lastName: z.string(),
  }),

  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

/** @typedef {z.infer<typeof savedTracerSchema>} SavedTracer */

const transactionFlowParams = z.object({
  chain: z.string(),
  transactionHash: evmTransactionHashSchema,
});
/** @typedef {z.infer<typeof transactionFlowParams>} TransactionFlowParams */

const transactionFlowRow = z.object({
  sourceAddress: enrichedAddressSchema,
  targetAddress: enrichedAddressSchema,
  tokenAddress: tokenDataSchema,
  flow: enrichedBalanceSchema,
});
/** @typedef {z.infer<typeof transactionFlowResults>} TransactionFlowRow */

const tokenFlowParams = z.object({
  chain: z.string(),
  filterAddress: enrichedAddressSchema,
  tokenAddress: tokenDataSchema,
  tokenType: z.enum(['native', 'erc20']),
  startTime: z.string().date(),
  endTime: z.string().date(),
});
/** @typedef {z.infer<typeof tokenFlowParams>} TokenFlowParams */
const tokenFlowRow = z.object({
  filterAddress: evmAddressSchema,
  walletAddress: enrichedAddressSchema,
  tokenAddress: tokenDataSchema.optional(),
  flow: enrichedBalanceSchema,
  interactionCount: z.number(),
});
/** @typedef {z.infer<typeof tokenFlowRow>} TokenFlowRow */

export const transactionFlowResults = z.object({
  params: transactionFlowParams,
  results: z.object({
    chain: z.string(),
    transactionHash: evmTransactionHashSchema,
    failed: z.boolean().default(false),
    txnTime: z.number(),
    tokenFlow: z.array(transactionFlowRow),
  }),
});
/** @typedef {z.infer<typeof transactionFlowResults>} TransactionFlowResults */

export const tokenFlowResults = z.object({
  params: tokenFlowParams,
  totalIn: enrichedBalanceSchema,
  totalOut: enrichedBalanceSchema,
  total: enrichedBalanceSchema,
  results: z.array(tokenFlowRow),
});
/** @typedef {z.infer<typeof tokenFlowResults>} TokenFlowResults */

/**
 * @typedef {z.infer<typeof tracerListRow>} TracerListRow
 */
